/**
 * `/config.json`: its shape, and the credential-shape detector applied to it.
 *
 * Two halves, in one file on purpose (#45).
 *
 * The **detector** — `credentialShapedValue` — is the first half. It is exported
 * because two other places need exactly it rather than a copy of it that has to be
 * kept in step by hand: `src/invariants/githubAppSecrets.test.ts`, which scans the
 * sources and the built bundle, and `scripts/validate-runtime-config.mjs`, which is
 * the gate that runs before `/config.json` is published. The copies this replaced had
 * already drifted into sharing a blind spot; one detector cannot drift from itself.
 *
 * The **schema** is the second half, and it is `strict()`: a key the schema does not
 * define fails the file, and every string it does define goes through the detector.
 * `editor-web` is served as static files and fetches `/config.json` unauthenticated,
 * so anything in that file is public. The Clinician Editor GitHub App credentials
 * belong to `editor-service` and reach it from Azure Key Vault through External
 * Secrets Operator; they have no field here at all.
 *
 * Note what refusing here does and does not buy. This runs **in the browser, at
 * boot**, by which point `/config.json` has been written and fetched — failing the
 * application closed protects the application, it does not un-disclose the value. That
 * is `scripts/validate-runtime-config.mjs`'s job, which applies this same schema
 * before the file is served.
 */
import { z } from "zod";

const PEM_PRIVATE_KEY = /-----BEGIN [A-Z ]*PRIVATE KEY-----/;
const GITHUB_TOKEN = /(gh[pousr]|github_pat)_[A-Za-z0-9_]{16,}/;
const OPAQUE_BLOB = /^[A-Za-z0-9+/=_-]{40,}$/;
const GIT_SHA = /^[0-9a-fA-F]{40}$/;

/**
 * The credential-shaped **runs** inside a token, as opposed to a token that is
 * credential-shaped in its entirety.
 *
 * The bypass this closes. Both entropy patterns used to be anchored to a whole
 * token, and a token is delimited by punctuation the writer of the value chooses.
 * So `leaked-<40 lowercase hex characters>` was neither a complete hex token nor a
 * mixed-case opaque one, and `credentialShapedValue()` returned `null` while the
 * credential sat in the value as a contiguous, copy-pasteable substring. Seven
 * characters of prefix defeated the whole heuristic — and the heuristic is the
 * *only* control left when the credential environment variables are absent, which
 * `scripts/validate-runtime-config.mjs` documents as the desired deployment state.
 *
 * `(?<!…)`/`(?!…)` make each match **maximal**: a run is reported once, at its full
 * length, rather than once per starting offset, so a 64-character digest is one
 * finding and not thirty-three.
 *
 * `HEX_RUN` is safe to hunt for anywhere: thirty-two consecutive characters drawn
 * only from `[0-9a-fA-F]` do not occur in a path, an identifier or prose.
 *
 * `OPAQUE_RUN` needs a narrower alphabet than `OPAQUE_BLOB`, because a *run* is not
 * bounded by punctuation the way a token is, and the base64 alphabet contains three
 * characters that ordinary code uses as joiners. Both exclusions were measured
 * against this repository's own sources and built bundle, not guessed:
 *
 * - `/` is out because including it made an ordinary import specifier a finding: a
 *   path such as `/features/…/ProjectOverview2` reaches 42 characters of lower case,
 *   upper case and a digit drawn entirely from the base64 alphabet well before it
 *   looks unusual to anyone reading it.
 * - `=` is out because including it joined a name to a content hash across an
 *   assignment. Every Vite chunk ends with a `sourceMappingURL=` comment naming a
 *   `<module>-<hash>.js.map` file, and the run spanning the property name, the `=`
 *   and the hashed filename was 47 characters of exactly the same mixture.
 *
 * Note that neither example is hypothetical and neither can be written out here in
 * full: a comment containing one is itself a 40-character mixed-case token in a file
 * this scan reads, so `src/invariants/githubAppSecrets.test.ts` reports it. That is
 * the detector working, and it is why both are elided above.
 *
 * Neither exclusion loses a secret, because the anchored `OPAQUE_BLOB` test over a
 * whole token — where `urlComponentTokens` has already isolated a URL's path segments
 * and query values intact — keeps both characters in its alphabet and still runs. The
 * run form is there for the one case a whole-token test cannot see: a base64 secret
 * fenced in by characters that are neither delimiters nor part of its alphabet, such
 * as `x~<40 mixed-case characters>`. The residual is a padded secret whose unpadded
 * length is 38 or 39 characters *and* which is fenced that way, which the anchored
 * test catches whenever the fence is a delimiter.
 */
const HEX_RUN = /(?<![0-9a-fA-F])[0-9a-fA-F]{32,}(?![0-9a-fA-F])/g;
const OPAQUE_RUN =
  /(?<![A-Za-z0-9+=_-])[A-Za-z0-9+_-]{40,}(?![A-Za-z0-9+=_-])/g;

/**
 * A run with base64 padding and separator characters trimmed off its ends.
 *
 * Only so that `looksLikeSourceIdentifier` gets a fair look at it. Minified code
 * contains `a!==someLongIdentifierName123`, whose maximal `OPAQUE_RUN` is
 * `==someLongIdentifierName123` — which fails `^[A-Za-z_$]` and so would be reported
 * as a credential purely because of the two `=` characters in front of it. Trimming
 * cannot hide a secret: base64 material does not become a pronounceable identifier by
 * losing its padding.
 */
const trimRunAffixes = (run: string): string =>
  run.replace(/^[+=_-]+/, "").replace(/[+=_-]+$/, "");

/**
 * The one field whose value is legitimately a 40-character hex string.
 *
 * The exemption has to be scoped to it. A field-agnostic "40 hex characters is a
 * Git SHA" rule exempts every 20-byte secret rendered as hex — which is exactly
 * what `openssl rand -hex 20` produces for a webhook secret — under any name at
 * all, so a credential pasted into `release.appVersion` would be accepted and
 * then served publicly from `/config.json`.
 */
const GIT_SHA_FIELDS = new Set([
  "release.commit",
  "release.serviceCommit",
  "release.deploymentCommit",
]);

/**
 * Fields whose value is a vendor-issued opaque *public* identifier.
 *
 * An OIDC client id is public by definition — it travels in every authorization
 * request — and identity providers issue them in shapes the entropy heuristics
 * look for, so applying "32 hex characters is a digest or a key" to them would
 * fail an otherwise valid deployment closed at boot.
 *
 * One field, not three. The ported version also listed `auth.audience` and
 * `tenant.id`; neither exists in this schema any more — `auth.audience` was
 * removed by schemaVersion 2 (#25, Entra rejects the Auth0-style `audience`
 * parameter) and the whole `tenant` block by schemaVersion 3 (#24). Carrying a
 * field name the schema does not define would be a dormant exemption waiting for
 * a future field to be given that name.
 *
 * `auth.apiScope` is deliberately absent: `api://<app-id>/access_as_user` is not
 * one of the two forms below, so listing it would grant nothing, and the
 * heuristics already pass an ordinary lower-case scope token.
 *
 * The heuristics stay in force on every other field, which is where a pasted
 * credential would actually land: a version string, a scope, a base URL.
 */
const OPAQUE_IDENTIFIER_FIELDS = new Set(["auth.clientId"]);

/**
 * The forms a value in `OPAQUE_IDENTIFIER_FIELDS` may take, checked **positively**
 * rather than the field being exempted from the heuristics.
 *
 * This is the difference between scoping a control and disarming it. A blanket
 * exemption returned before either entropy check ran, so the listed fields
 * accepted *any* shape at all below the PEM and token signatures — including the
 * exact one the detector's own docstring names as a realistic secret: 40 hex
 * characters, which is what `openssl rand -hex 20` produces for a webhook secret.
 * A credential pasted into `auth.clientId` was therefore served publicly from
 * `/config.json` by the very field list that was meant to be the control's "correct
 * scope".
 *
 * Positive validation has no such hole: a value that is not recognisably a public
 * identifier is put back through the heuristics, so 40 hex characters is refused
 * everywhere. The forms are the two that actually need an allowance, because
 * everything shorter than 40 characters passes the heuristics anyway:
 *
 * - a canonical GUID, which is how Entra ID presents an "Application (client) ID";
 * - the same GUID pasted without its hyphens, 32 hex characters, which is the one
 *   real collision with `HEX_BLOB`.
 *
 * A 40-character mixed-case opaque id is deliberately **not** here. It is
 * indistinguishable from 30 bytes of base64 secret, and no provider we use issues
 * one; if one starts, add its exact documented form to this list rather than
 * widening the shape.
 *
 * Residual, and stated rather than papered over: 16 bytes of hex secret is
 * indistinguishable from a dashless GUID, so it would pass in `auth.clientId`.
 * `strict()` is what makes that acceptable — the App credentials have no field in
 * this schema at all — and repository secret scanning is the backstop.
 */
const PUBLIC_IDENTIFIER_FORMS = [
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
  /^[0-9a-fA-F]{32}$/,
];

const isPublicIdentifier = (field: string, text: string): boolean =>
  OPAQUE_IDENTIFIER_FIELDS.has(field) &&
  PUBLIC_IDENTIFIER_FORMS.some((form) => form.test(text));

/**
 * Three tokenizations, because no single delimiter set finds every shape.
 *
 * A base64 secret contains `/`, `+` and `=`, so splitting on URL punctuation
 * would cut `abc/def…` into two short halves and see neither. A secret embedded
 * in a URL — `https://host/<64 hex characters>` or `?secret=<blob>` — is one
 * whitespace-delimited word, so splitting only on whitespace and quotes sees a
 * word that matches no anchored pattern. Both are real: `z.url()` accepts the
 * second, so a webhook secret parked in a URL path or query passed every check
 * this file makes.
 *
 * And the two of them together still missed the intersection of the two cases: a
 * **base64 secret inside a URL**. The whitespace tokenization keeps `/`, `+` and
 * `=` intact, but the token it produces is the entire URL and so matches no
 * anchored pattern; the URL tokenization isolates the query value but splits the
 * base64 on the same three characters that make it base64. So
 *
 *   https://example.invalid/?secret=AbCd1234/AbCd1234/…/AbCd1234==
 *
 * carried a mixed-case 40-plus-character secret past both. The third tokenization
 * closes it by splitting URLs *structurally* — into whole path segments and whole
 * query and fragment values — and keeping each one intact, so the base64
 * punctuation inside a value is never a delimiter.
 *
 * A match in any of the three is a finding. The patterns stay anchored to a whole
 * token, which is what keeps a hostname label or a Vault path from matching a
 * fragment of itself.
 */
const WORD_DELIMITERS = /[\s"'`,;(){}[\]<>]+/;
const URL_DELIMITERS = /[\s"'`,;(){}[\]<>:/?&#=@|\\.]+/;

/** URL-looking substrings, wherever they sit — including inside serialized JSON. */
const URL_LIKE = /[a-z][a-z0-9+.-]*:\/\/[^\s"'`\\<>{}|^]*/gi;

/**
 * `[url, trailing]` — `url` with prose punctuation that cannot belong to it split off.
 *
 * `URL_LIKE` has to be permissive on the right-hand side, because `.`, `,`, `)` and
 * `]` are all legal URL characters and a secret parked in a path or query must not
 * be cut short by one of them. The cost is that a URL written in prose absorbs the
 * punctuation that follows it, and that punctuation lands on the *last* path
 * segment — which is exactly where `maskForgeCommitIds` looks for a commit id.
 *
 * So `(https://github.com/o/r/blob/<sha>)`, `[source](https://…/blob/<sha>)` and a
 * permalink ending a sentence all produced a final segment of `<sha>)` or `<sha>.`,
 * which is not 40 hex characters and so was not masked; the word tokenizer then
 * split the punctuation off again and reported the commit id as a leaked hex key.
 * Markdown-linked and parenthesised permalinks are ordinary in dependency source,
 * which the bundle scan reads, so an unrelated dependency bump broke CI.
 *
 * Trailing `.,;:!?'"` is always prose. A closing bracket is prose only when the
 * matched text does not itself open it, which is what keeps a genuine
 * `…/Foo_(disambiguation)` intact. Note that this only ever *narrows* the URL: the
 * characters split off are re-appended by the caller and stay in the scanned text,
 * so nothing escapes tokenization by being trimmed here.
 */
const PROSE_TERMINATORS = ".,;:!?'\"";
const BRACKET_PAIRS: Record<string, string> = { ")": "(", "]": "[" };

/**
 * Bracket balance is tracked in one backwards pass, not recomputed per closer.
 *
 * The earlier form called `split()` twice on `url.slice(0, end)` for every trailing
 * `)` or `]`, so a value ending in *n* closers rescanned O(n) characters n times.
 * Measured, that is 383 ms at 8,000 closers and 1.35 s at 16,000 — and both the
 * runtime configuration file and every scanned source file are inputs to this path,
 * so a single moderately large value could stall the pre-publication gate, CI or
 * browser boot. The counts here are computed once over the whole string and
 * decremented as characters are dropped, which is the same decision at every step
 * for a linear total.
 */
function splitTrailingProse(url: string): [string, string] {
  const counted: Record<string, number> = { "(": 0, ")": 0, "[": 0, "]": 0 };
  for (const character of url) {
    if (counted[character] !== undefined) counted[character] += 1;
  }
  let end = url.length;
  // `counted` always describes `url.slice(0, end)`.
  const drop = () => {
    end -= 1;
    const dropped = url[end] as string;
    if (counted[dropped] !== undefined) counted[dropped] -= 1;
  };
  while (end > 0) {
    const character = url[end - 1] as string;
    if (PROSE_TERMINATORS.includes(character)) {
      drop();
      continue;
    }
    const opener = BRACKET_PAIRS[character];
    if (
      opener !== undefined &&
      (counted[character] ?? 0) > (counted[opener] ?? 0)
    ) {
      drop();
      continue;
    }
    break;
  }
  return [url.slice(0, end), url.slice(end)];
}

/**
 * The character ranges of `text` that are the contents of a string or template
 * literal, for the two source-code allowances that must not apply inside one.
 *
 * The bypass this closes, and it applied to both allowances. Each of them decided
 * that some text was *not* a credential from the text's own spelling — a 40-hex value
 * sitting after `blob` in a forge-looking URL, or a token whose word structure reads
 * like an identifier — and spelling is chosen by whoever wrote the file. So both of
 * these returned `null` while the credential sat in the source as a contiguous,
 * runtime-readable string literal:
 *
 *   new URL("https://github.com/acme/repo/blob/<40-hex-secret>/x").pathname.split("/")[5]
 *   const leaked = "CorrectHorseBatteryStapleValueWithNumber1";
 *
 * A webhook secret is user-selected, so the second shape is one an operator can
 * plausibly *have*; the first needs only a URL that looks like a permalink. Neither
 * position is evidence of anything, because in both cases the program can read the
 * value back at runtime.
 *
 * What the allowances are actually for is the case where the text is **not** a value
 * the program can use: a permalink in a dependency's comment, an identifier that *is*
 * a declared name rather than the contents of a string. Both of those live outside
 * string literals, so that is where the allowances are scoped to. Inside a literal,
 * the entropy heuristics apply with no exemption at all.
 *
 * Lexed rather than pattern-matched because a quote inside a comment or a regular
 * expression would otherwise open a phantom literal and desynchronise everything
 * after it — `don't` in a comment and `/'/g` in minified code are both ordinary.
 * Interpolations are lexed as code, so a template literal contributes one span per
 * literal chunk and `${…}` is not covered by any of them.
 *
 * An unterminated literal runs to the end of the text: a scanner that guesses the
 * other way would let a single trailing quote re-arm the exemptions for the rest of
 * the file.
 */
interface Span {
  start: number;
  end: number;
}

/**
 * Keywords a regular expression may directly follow, so `return/x/.test(s)` — which
 * minifiers emit — is not read as a division whose "operand" swallows the next quote.
 */
const REGEX_KEYWORDS = new Set([
  "await",
  "case",
  "delete",
  "do",
  "else",
  "in",
  "instanceof",
  "new",
  "of",
  "return",
  "throw",
  "typeof",
  "void",
  "yield",
]);

/** Operators and punctuation a regular expression may directly follow. */
const REGEX_PRECEDERS = "(,=:[!&|?{};+-*%^~<>";

const IDENTIFIER_CHARACTER = /[A-Za-z0-9_$]/;

function sourceLiteralSpans(text: string): Span[] {
  const spans: Span[] = [];
  /**
   * One frame per template literal being lexed. `braces` is `-1` while the
   * template's own text is being read, and the interpolation's brace depth while an
   * `${…}` is being lexed as code.
   */
  const templates: { start: number; braces: number }[] = [];
  let index = 0;
  /** Last significant character, and the word it ended, for the regex/division call. */
  let previous = "";
  let word = "";

  const template = () => templates[templates.length - 1];

  while (index < text.length) {
    const frame = template();
    const character = text[index] as string;

    if (frame !== undefined && frame.braces === -1) {
      // Inside a template literal's text.
      if (character === "\\") {
        index += 2;
        continue;
      }
      if (character === "`") {
        spans.push({ start: frame.start, end: index + 1 });
        templates.pop();
        previous = "`";
        word = "";
        index += 1;
        continue;
      }
      if (character === "$" && text[index + 1] === "{") {
        spans.push({ start: frame.start, end: index + 2 });
        frame.braces = 0;
        previous = "";
        word = "";
        index += 2;
        continue;
      }
      index += 1;
      continue;
    }

    // Code: the top level, or an interpolation inside a template literal.
    if (character === "/" && text[index + 1] === "/") {
      const newline = text.indexOf("\n", index);
      index = newline === -1 ? text.length : newline;
      continue;
    }
    if (character === "/" && text[index + 1] === "*") {
      const close = text.indexOf("*/", index + 2);
      index = close === -1 ? text.length : close + 2;
      continue;
    }
    if (character === '"' || character === "'") {
      const start = index;
      index += 1;
      while (index < text.length) {
        const inside = text[index] as string;
        if (inside === "\\") {
          index += 2;
          continue;
        }
        if (inside === character) {
          index += 1;
          break;
        }
        // A single- or double-quoted literal cannot contain a raw newline, so an
        // unterminated one ends at the line break rather than swallowing the file.
        if (inside === "\n") break;
        index += 1;
      }
      spans.push({ start, end: index });
      previous = character;
      word = "";
      continue;
    }
    if (character === "`") {
      templates.push({ start: index, braces: -1 });
      index += 1;
      continue;
    }
    if (
      character === "/" &&
      (word !== ""
        ? REGEX_KEYWORDS.has(word)
        : previous === "" || REGEX_PRECEDERS.includes(previous))
    ) {
      const start = index;
      index += 1;
      let inClass = false;
      let closed = false;
      while (index < text.length) {
        const inside = text[index] as string;
        if (inside === "\\") {
          index += 2;
          continue;
        }
        if (inside === "\n") break;
        if (inClass) {
          if (inside === "]") inClass = false;
        } else if (inside === "[") {
          inClass = true;
        } else if (inside === "/") {
          index += 1;
          closed = true;
          break;
        }
        index += 1;
      }
      if (closed) {
        previous = "/";
        word = "";
        continue;
      }
      // Not a regular expression after all: rewind and treat it as an operator.
      index = start + 1;
      previous = "/";
      word = "";
      continue;
    }
    if (frame !== undefined) {
      if (character === "{") frame.braces += 1;
      else if (character === "}") {
        if (frame.braces === 0) {
          // The interpolation closed; the template's text resumes at this `}`.
          frame.braces = -1;
          frame.start = index;
          index += 1;
          continue;
        }
        frame.braces -= 1;
      }
    }
    if (IDENTIFIER_CHARACTER.test(character)) {
      word += character;
    } else {
      word = "";
    }
    if (!/\s/.test(character)) previous = character;
    index += 1;
  }

  // An unterminated template literal covers everything that follows it.
  const unterminated = template();
  if (unterminated !== undefined && unterminated.braces === -1) {
    spans.push({ start: unterminated.start, end: text.length });
  }
  return spans;
}

/** Whether `offset` falls inside one of `spans`, which are produced in order. */
function isInsideSpan(spans: Span[], offset: number): boolean {
  let low = 0;
  let high = spans.length - 1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const span = spans[middle] as Span;
    if (offset < span.start) high = middle - 1;
    else if (offset >= span.end) low = middle + 1;
    else return true;
  }
  return false;
}

/**
 * Path positions in a forge URL where a 40-character hex value is a commit id.
 *
 * `https://github.com/nodejs/undici/blob/<sha>/lib/web/…` is a permalink, and
 * permalinks are everywhere in dependency source comments — which the bundle scan
 * reads, because Vite's source maps carry `sourcesContent` for every module in a
 * chunk. Without this the scan reports every vendored permalink as a hex key.
 *
 * A **structural** allowance, not a field-agnostic one: the value must be exactly 40
 * hex characters *and* sit directly after one of these path components on a
 * recognised forge host, in a URL that is **not inside a string literal**. That is
 * the same shape `release.commit` is allowed to hold, in the one other place it
 * legitimately appears. A field-agnostic "40 hex is a Git SHA" rule is what this file
 * refuses to have, because it would exempt every 20-byte hex secret under any name at
 * all — and the position alone is not much better, which is what `sourceLiteralSpans`
 * exists to fix: a permalink the program can *read* is not evidence of a commit.
 */
const FORGE_HOSTS =
  /(?:^|\.)(?:github\.com|githubusercontent\.com|gitlab\.com|bitbucket\.org)$/i;
const COMMITISH_PATH_COMPONENTS = new Set([
  "blob",
  "blame",
  "commit",
  "commits",
  "raw",
  "tree",
]);

/**
 * A token, with where it came from.
 *
 * The offset is what decides whether a source-code allowance applies to it: an
 * exemption earned by spelling has to be checked against the syntactic position the
 * spelling sits in. See `sourceLiteralSpans`.
 */
interface Token {
  text: string;
  /** Offset into the text being scanned. For a URL component and for a decoded
   *  form, the offset of the URL or of the token it was derived from. */
  start: number;
}

/**
 * `text` split on `delimiters`, each piece carrying its offset.
 *
 * `String.prototype.split` loses the positions, and the positions are load-bearing
 * now, so the split is done with the delimiter pattern in global form instead.
 */
function splitTokens(text: string, delimiters: RegExp, base: number): Token[] {
  const pattern = new RegExp(
    delimiters.source,
    delimiters.flags.includes("g") ? delimiters.flags : `${delimiters.flags}g`,
  );
  const tokens: Token[] = [];
  let index = 0;
  for (const match of text.matchAll(pattern)) {
    tokens.push({ text: text.slice(index, match.index), start: base + index });
    index = match.index + match[0].length;
  }
  tokens.push({ text: text.slice(index), start: base + index });
  return tokens;
}

/**
 * Whole path segments and whole query/fragment values of every URL in `text`.
 *
 * Parsed textually rather than with `new URL`, on purpose: a malformed URL is the
 * case that most needs scanning, and a parser that throws would skip it. Nothing is
 * percent-decoded and nothing is split on `/`, `+` or `=` inside a value, so a
 * base64 secret arrives here in one piece.
 */
function urlComponentTokens(text: string, base: number): Token[] {
  const tokens: Token[] = [];
  let at = 0;
  const push = (value: string) =>
    tokens.push({ text: value, start: base + at });
  const values = (pairs: string) => {
    for (const pair of pairs.split("&")) {
      const separator = pair.indexOf("=");
      // The value, and the whole pair: a secret can be the value of `?s=…`, and it
      // can also be a bare `?<blob>` with no `=` at all.
      push(separator === -1 ? pair : pair.slice(separator + 1));
      push(pair);
    }
  };
  for (const match of text.matchAll(URL_LIKE)) {
    const matched = match[0];
    // Every component of a URL is attributed to the position of the URL itself: the
    // question an offset answers is which syntactic construct the value sits in, and
    // a URL does not straddle one.
    at = match.index;
    // Prose punctuation split off for the same reason as in `maskForgeCommitIds`:
    // a trailing `)` would otherwise be glued onto the last path segment. The
    // trimmed characters are still tokenized by the word/URL splits above.
    const [url] = splitTrailingProse(matched);
    const afterScheme = url.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");
    const hash = afterScheme.indexOf("#");
    const beforeFragment =
      hash === -1 ? afterScheme : afterScheme.slice(0, hash);
    if (hash !== -1) values(afterScheme.slice(hash + 1));
    const question = beforeFragment.indexOf("?");
    const pathPart =
      question === -1 ? beforeFragment : beforeFragment.slice(0, question);
    if (question !== -1) values(beforeFragment.slice(question + 1));

    const segments = pathPart.split("/");
    // The whole path as one token, so a base64 secret spread across `/`-separated
    // segments is seen. Splitting on `/` alone cut `AbCd1234/AbCd1234/…` into
    // eight-character pieces that match nothing.
    push(segments.slice(1).join("/"));
    for (const segment of segments) push(segment);
  }
  return tokens;
}

/**
 * `text` with the commit id of every forge permalink replaced by a placeholder.
 *
 * The bypass this closes, and it is why the allowance masks *occurrences* rather
 * than collecting values. Suppression used to be by value: every 40-hex token that
 * appeared in a commit position anywhere in the text was exempted *everywhere* in
 * the text. So
 *
 *   const leaked = "<40-hex-secret>";
 *   // https://github.com/x/y/blob/<the same 40-hex-secret>/z
 *
 * returned `null` — a secret plus one line of attacker-chosen comment was enough to
 * exempt the secret, and a comment is not a thing this scan can trust. Sixty-eight
 * characters of "legitimate-looking permalink" is a cheap price for publishing a
 * webhook secret.
 *
 * Removing the permalink's own commit id and rescanning the remainder has no such
 * property: the exemption is tied to the position it was earned in, and an identical
 * token anywhere else is still a finding. The placeholder is outside every alphabet
 * the blob patterns use, so it cannot rejoin a neighbouring token to form one.
 *
 * It is also exactly as long as the commit id it replaces, so every offset after it is
 * unchanged and the literal spans computed over the original text still describe the
 * masked one. That matters because the mask itself is now scoped by those spans: a
 * permalink inside a string literal is a value the program can read back, so it is not
 * masked at all. See `sourceLiteralSpans`.
 */
const COMMIT_ID_PLACEHOLDER = "<commit-id-elided>".padEnd(40, ".");

/**
 * The one property name a built bundle may carry a commit id under (#490).
 *
 * `apps/editor-web/src/config/buildInfo.ts` bakes the commit the bundle was
 * built from into `EDITOR_WEB_BUILD`, and Vite's `define` substitutes it
 * TEXTUALLY — so what reaches `dist/` is a 40-character hex string literal,
 * which `HEX_RUN` reports as "a hex-encoded digest or key". Without an
 * allowance the mandatory built-bundle credential scan would fail on every
 * published build, and the only way to make it pass would be to stop shipping
 * the identity — which is the freshness gap #490 exists to close.
 *
 * WHY THE ALLOWANCE IS KEYED ON THE PROPERTY NAME rather than on the value's
 * shape alone. "40 hex characters is a commit id" is precisely the rule this
 * file refuses to have anywhere: `openssl rand -hex 20` produces a webhook
 * secret of exactly that shape. So the exemption is granted to the value in ONE
 * syntactic position — immediately after the `editorWebSourceCommit` key — for
 * the same reason `release.commit` earns one in `/config.json`, and it is
 * checked with the same `GIT_SHA` shape test on top. Minifiers rename local
 * bindings but not object property keys, which is what makes the marker survive
 * into `dist/` and why `buildInfo.ts` exports an object rather than a constant.
 *
 * SOURCE-SCANNING ONLY, exactly like the permalink mask below and for the same
 * reason: nothing in `/config.json` has a reason to carry this property, so
 * applying the allowance there would create a second name under which a
 * credential could be smuggled into a public runtime configuration file.
 *
 * THE BACKTICK IS NOT DECORATION. The minifier that produces `dist/` rewrites
 * this property's value as a TEMPLATE literal — `editorWebSourceCommit:\`<sha>\``
 * — so a pattern admitting only `"` and `'` matches the source and misses the
 * built bundle, which is the only place the value is actually published. That
 * is the direction that fails closed (the scan reports the commit as a leaked
 * key), so it would have been caught; it is spelled out because the next person
 * to tighten this pattern needs to know why three quote characters are listed.
 *
 * Length-preserving, so the literal spans computed over the unmasked text still
 * describe the masked text.
 */
const BUILD_COMMIT_LITERAL =
  /(["']?)editorWebSourceCommit\1\s*:\s*(["'`])([0-9a-fA-F]{40})\2/g;

function maskBuildCommitIds(text: string): string {
  return text.replace(
    BUILD_COMMIT_LITERAL,
    (matched, _keyQuote: string, valueQuote: string, commit: string) =>
      GIT_SHA.test(commit)
        ? matched.slice(0, matched.length - commit.length - valueQuote.length) +
          COMMIT_ID_PLACEHOLDER +
          valueQuote
        : matched,
  );
}

function maskForgeCommitIds(text: string, literals: Span[]): string {
  return text.replace(URL_LIKE, (matched, offset: number) => {
    // A permalink the program can read is not evidence of a commit:
    //
    //   new URL("https://github.com/acme/repo/blob/<40-hex-secret>/x").pathname.split("/")[5]
    //
    // masked the credential out of the text before any check saw it, and the code
    // around it extracts the same 40 characters at runtime. The allowance exists for
    // permalinks in comments and prose, which is where dependency source keeps them.
    if (isInsideSpan(literals, offset)) return matched;
    // The punctuation a prose or Markdown context puts after the URL is not part
    // of it, and leaving it attached made the last path segment `<sha>)` — which
    // is not a commit id, so the permalink went unmasked and its commit id was
    // reported as a leaked hex key. See `splitTrailingProse`.
    const [url, trailingProse] = splitTrailingProse(matched);
    const scheme = /^[a-z][a-z0-9+.-]*:\/\//i.exec(url)?.[0] ?? "";
    const afterScheme = url.slice(scheme.length);
    const queryAt = afterScheme.search(/[?#]/);
    const pathPart =
      queryAt === -1 ? afterScheme : afterScheme.slice(0, queryAt);
    const suffix = queryAt === -1 ? "" : afterScheme.slice(queryAt);
    const segments = pathPart.split("/");
    const host = (segments[0] ?? "").replace(/:\d+$/, "").replace(/^.*@/, "");
    if (!FORGE_HOSTS.test(host)) return matched;
    const masked = segments.map((segment, index) =>
      GIT_SHA.test(segment) &&
      COMMITISH_PATH_COMPONENTS.has((segments[index - 1] ?? "").toLowerCase())
        ? COMMIT_ID_PLACEHOLDER
        : segment,
    );
    return `${scheme}${masked.join("/")}${suffix}${trailingProse}`;
  });
}

/**
 * Percent-decoding, applied repeatedly, returning every intermediate form.
 *
 * The bypass this closes, reproduced end to end before it was fixed:
 *
 *   https://example.invalid/?secret=%30%31%32…
 *
 * `urlComponentTokens` deliberately decodes nothing — a malformed escape must not
 * make a value unscannable — so the query value it isolated was `%30%31%32…`, which
 * has no 32-character hex run and no 40-character opaque run in it. Every check in
 * this file passed, `z.url()` passed, and the pre-publication validator's literal
 * substring comparison passed too. A browser, or anyone at all with `curl`, decodes
 * the query and gets the credential back verbatim.
 *
 * Whole escape *runs* are decoded as UTF-8, and that correction matters as much as the
 * decoding itself. Decoding each `%XX` on its own with `String.fromCharCode` treats
 * every byte as a code point, so a credential containing a non-ASCII character came
 * back mojibaked rather than decoded: `quiet%C3%A9secret` became `quietÃ©secret`, which
 * does not contain `quietésecret`. The pre-publication gate compares the credential's
 * *exact value*, so a configuration carrying a reversibly encoded copy of a secret with
 * one accented character in it was reported as publishable. Passwords and webhook
 * secrets are chosen by people, and people use accents.
 *
 * `decodeURIComponent` is what knows UTF-8, and it throws on a lone `%` or on bytes
 * that are not valid UTF-8 — so it is applied per run, inside a `try`, with the old
 * byte-per-escape decoding as the fallback. A scanner that throws is a scanner that
 * skipped the file; a scanner that silently mangles is one that missed the credential.
 *
 * Repeated because a consuming stack can decode more than once: a value that reaches
 * the browser through one decode and a template through another needs `%2530` — one
 * extra round — to hide from a single-pass scanner. Three rounds is past anything a
 * real stack does, and the loop stops as soon as a round changes nothing.
 */
const PERCENT_ESCAPE = /%[0-9a-fA-F]{2}/;
const PERCENT_ESCAPES = /%([0-9a-fA-F]{2})/g;
/** A maximal run of escapes, so a multi-byte UTF-8 sequence is decoded as one. */
const PERCENT_ESCAPE_RUN = /(?:%[0-9a-fA-F]{2})+/g;
const MAX_PERCENT_DECODE_ROUNDS = 3;

const decodePercentRun = (run: string): string => {
  try {
    return decodeURIComponent(run);
  } catch {
    // Not valid UTF-8. Each escape still becomes the byte it names, which is all
    // ASCII credential material — base64, base64url, hex — ever needs.
    return run.replace(PERCENT_ESCAPES, (_matched, hex: string) =>
      String.fromCharCode(Number.parseInt(hex, 16)),
    );
  }
};

/**
 * Every intermediate percent-decoded form of `text`, decoded as UTF-8.
 *
 * Exported so that `scripts/validate-runtime-config.mjs` compares against the same
 * decodings the heuristics scan, rather than a second copy of this logic that has to be
 * kept in step by hand — the two copies had already drifted into sharing the
 * byte-per-escape bug above.
 */
export function percentDecodedForms(text: string): string[] {
  const forms: string[] = [];
  let current = text;
  for (let round = 0; round < MAX_PERCENT_DECODE_ROUNDS; round += 1) {
    if (!PERCENT_ESCAPE.test(current)) break;
    const next = current.replace(PERCENT_ESCAPE_RUN, decodePercentRun);
    if (next === current) break;
    forms.push(next);
    current = next;
  }
  return forms;
}

function tokensOf(text: string): Token[] {
  const tokens = [
    ...splitTokens(text, WORD_DELIMITERS, 0),
    ...splitTokens(text, URL_DELIMITERS, 0),
    ...urlComponentTokens(text, 0),
  ];
  // Decoding happens after tokenization and is *added* to it, never substituted for
  // it: the encoded form has to stay scanned, because a credential can also be stored
  // percent-encoded and read back that way. A decoded form is re-split as well, since
  // decoding can produce the very delimiters that separate one value from the next
  // (`%26` is `&`, `%3D` is `=`).
  //
  // A decoded form has no offsets of its own, so it inherits the position of the token
  // it came from — which is the position any source-code allowance has to be judged
  // against anyway.
  const decoded: Token[] = [];
  for (const token of tokens) {
    for (const form of percentDecodedForms(token.text)) {
      for (const part of [
        form,
        ...form.split(WORD_DELIMITERS),
        ...form.split(URL_DELIMITERS),
      ]) {
        decoded.push({ text: part, start: token.start });
      }
    }
  }
  return [...tokens, ...decoded];
}

/**
 * Word boundaries inside a source identifier: a capital, or a letter/digit
 * transition, or `_`/`$`.
 */
const IDENTIFIER_WORDS =
  /(?=[A-Z])|(?<=[A-Za-z])(?=[0-9])|(?<=[0-9])(?=[A-Za-z])|[_$]/;

/**
 * `true` if `token` is a long source-code identifier rather than a credential.
 *
 * Needed only because `dist/` source maps are now scanned: `sourcesContent` is
 * dependency source, and a bundled library really does contain identifiers like
 * `handleAndDispatchPointerDownOutsideEvent2` — 41 characters with lower case, upper
 * case and a digit, which is the entire `OPAQUE_BLOB` test. Without a discriminator
 * the choice was reporting every such identifier or, as before, not scanning source
 * maps at all and publishing whatever `sourcesContent` carried.
 *
 * Shannon entropy does not separate the two: a camelCase identifier of this length
 * measures ~4.2 bits/character and random 40-character base64 bottoms out around
 * ~4.3, and a *repetitive* base64 secret measures lower than either. What does
 * separate them is word structure. An identifier is a concatenation of pronounceable
 * words — split it on the boundaries above and every word of three or more letters
 * has a vowel — and it carries almost no digits, while 40 random base64 characters
 * carry about six.
 *
 * So: identifier characters only, at most two digits, at least four alphabetic
 * words, and a vowel in every word of three letters or more. Measured against 500,000
 * random secrets per encoding, that misclassifies 0.32% of base64url, 0.13% of
 * base64, and none of hex. That residual is the price of scanning source maps at all,
 * and it is paid only there: `sourceCode` is off by default, so nothing arriving in
 * `/config.json` gets this allowance.
 *
 * Spelling alone is not enough, though, and this is only half of the test. A webhook
 * secret is *user-selected*, so `CorrectHorseBatteryStapleValueWithNumber1` is a
 * perfectly possible one and has exactly the word structure described above — which
 * made `const leaked = "CorrectHorseBatteryStapleValueWithNumber1";` return `null`.
 * The other half is the syntactic position: the caller applies this only to a token
 * that is not inside a string literal, so it exempts declared and referenced names,
 * never the contents of a quoted string. See `sourceLiteralSpans`.
 */
function looksLikeSourceIdentifier(token: string): boolean {
  if (!/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(token)) return false;
  if ((token.match(/[0-9]/g) ?? []).length > 2) return false;
  const words = token
    .split(IDENTIFIER_WORDS)
    .filter((word) => /^[A-Za-z]+$/.test(word));
  if (words.length < 4) return false;
  return words.every((word) => word.length < 3 || /[aeiouyAEIOUY]/.test(word));
}

/**
 * `null`, or a description of the credential shape `text` carries.
 *
 * Exported so that `src/invariants/githubAppSecrets.test.ts` scans the sources
 * and the built bundle with exactly this detector rather than a copy of it that
 * has to be kept in step by hand — the two copies had already drifted into
 * sharing the same blind spot.
 *
 * `binary` suppresses the entropy heuristics: compressed bytes are high-entropy
 * by construction, so they would fire on every image and prove nothing. The two
 * signatures survive embedding in binary and stay on.
 */
export function credentialShapedValue(
  text: string,
  {
    field = "",
    binary = false,
    sourceCode = false,
  }: { field?: string; binary?: boolean; sourceCode?: boolean } = {},
): string | null {
  if (PEM_PRIVATE_KEY.test(text)) return "a PEM private key";
  if (GITHUB_TOKEN.test(text)) return "a GitHub token";
  if (binary) return null;
  // Positive validation, not an exemption: a value that is not recognisably a
  // public identifier goes through the heuristics like any other. See
  // `PUBLIC_IDENTIFIER_FORMS`.
  if (isPublicIdentifier(field, text)) return null;

  // The one hex allowance a `/config.json` value gets, and it is earned by the whole
  // value rather than by the field's name alone. `release.commit` holds a commit id
  // and nothing else, so `field === GIT_SHA_FIELD` on its own exempted
  // `deploy-<40-hex-webhook-secret>` in that field — the credential was a substring,
  // and only the substring needed to look like a SHA.
  const commitAllowed = GIT_SHA_FIELDS.has(field) && GIT_SHA.test(text);
  // The forge-permalink allowance is **source-scanning only**, and that scoping is the
  // point of the `sourceCode` flag rather than an optimisation.
  //
  // It exists because `dist/` source maps carry `sourcesContent`, and dependency
  // source is full of `https://github.com/o/r/blob/<sha>/file.ts` permalinks. It is
  // also, unavoidably, a rule that deletes 40 hex characters from the text before any
  // credential check sees them — so while it ran unconditionally, it applied to
  // `/config.json` too, and
  //
  //   https://github.com/acme/repo/blob/<40-hex-webhook-secret>/file.ts
  //
  // was accepted in an ordinary field. Nothing in a runtime configuration file has a
  // reason to carry a forge permalink, and the one place it legitimately holds 40 hex
  // characters is `release.commit`, which has its own field-scoped exception above.
  //
  // Where it does apply, it works by *removing* each legitimate commit id from the text
  // and tokenizing what is left, not by collecting the values and exempting them
  // wherever else they appear — the second form let one attacker-written comment line
  // exempt the identical value elsewhere in the file. See `maskForgeCommitIds`.
  //
  // Both source-code allowances — the permalink mask and the identifier exemption
  // below — are additionally scoped to text that is *not* inside a string literal,
  // because in both cases the spelling that earns the exemption is chosen by whoever
  // wrote the file and a quoted string is a value the program can read at runtime. The
  // mask is length-preserving, so these spans describe the masked text too.
  const literals = sourceCode ? sourceLiteralSpans(text) : [];
  // The build-commit mask runs BEFORE the permalink mask and independently of
  // the literal spans, because the value it exempts IS inside a string literal
  // — that is the whole difference between the two allowances. The permalink
  // one is earned by a spelling the author chose in prose, so it is refused
  // inside literals; this one is earned by the property name the program reads
  // the value under, which is a position a credential cannot occupy by
  // accident. Both are length-preserving, so `literals` still describes the
  // text either of them returns.
  const words = tokensOf(
    sourceCode ? maskForgeCommitIds(maskBuildCommitIds(text), literals) : text,
  );

  for (const word of words) {
    const quoted = sourceCode && isInsideSpan(literals, word.start);
    // The whole token, over an alphabet that includes `/`, plus every maximal run
    // inside it over the same alphabet without `/`. See `OPAQUE_RUN` for why the two
    // are not one check.
    const candidates = OPAQUE_BLOB.test(word.text)
      ? [word.text, ...[...word.text.matchAll(OPAQUE_RUN)].map(([run]) => run)]
      : [...word.text.matchAll(OPAQUE_RUN)].map(([run]) => run);
    for (const candidate of candidates) {
      if (
        /[a-z]/.test(candidate) &&
        /[A-Z]/.test(candidate) &&
        /[0-9]/.test(candidate) &&
        !(
          sourceCode &&
          !quoted &&
          looksLikeSourceIdentifier(trimRunAffixes(candidate))
        )
      ) {
        // No fragment of `candidate` is quoted. This description is carried into a Zod
        // issue message, and `scripts/validate-runtime-config.mjs` prints issue
        // messages into a deployment log: an eight-character prefix of a rejected
        // credential in that log is the disclosure the whole gate exists to
        // prevent. The field path in the issue is what locates the value.
        return "an opaque high-entropy value";
      }
    }
  }
  for (const word of words) {
    for (const [run] of word.text.matchAll(HEX_RUN)) {
      if (commitAllowed && GIT_SHA.test(run)) continue;
      return "a hex-encoded digest or key";
    }
  }
  return null;
}

/**
 * Adds a custom issue whose message is guaranteed to contain no input material.
 *
 * `scripts/validate-runtime-config.mjs` prints validation failures into a
 * deployment log, and it refuses to print a Zod `message` it does not recognise —
 * because a rejected value is by definition the kind that must not be logged, and
 * Zod's built-in messages quote input (`Unrecognized key: "<the value>"`). The
 * `params.safeMessage` marker is that recognition: it is set only here, only from
 * text this file composed, and the validator echoes nothing else.
 *
 * Anything interpolated into `safeMessage` must therefore already be constrained by
 * the schema — an enum member, a field name — never a free-form string from the
 * file under inspection.
 */
function addSafeIssue(
  ctx: z.RefinementCtx,
  path: (string | number)[],
  safeMessage: string,
): void {
  ctx.addIssue({
    code: "custom",
    path,
    message: safeMessage,
    params: { safeMessage },
  });
}

function rejectCredentialValues(
  value: unknown,
  path: (string | number)[],
  ctx: z.RefinementCtx,
): void {
  if (typeof value === "string") {
    const finding = credentialShapedValue(value, { field: path.join(".") });
    if (finding !== null) {
      // `finding` is one of a fixed set of shape descriptions and quotes no part of
      // the rejected value — see `credentialShapedValue`.
      addSafeIssue(ctx, path, `value looks like ${finding}`);
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      rejectCredentialValues(item, [...path, index], ctx);
    }
    return;
  }
  if (value !== null && typeof value === "object") {
    for (const [key, nested] of Object.entries(value)) {
      rejectCredentialValues(nested, [...path, key], ctx);
    }
  }
}

/**
 * Shape of /config.json.
 *
 * Written into the served directory by the container entrypoint (issue #23).
 * There are no defaults: an absent or malformed value is an error that fails
 * the application closed, never a fallback that silently starts the editor
 * pointing somewhere unintended.
 */

/**
 * Environments the Clinician Editor may run in.
 *
 * `production` is intentionally absent. ADR 0001 decision 9 states that
 * production structurally refuses preview; the browser refuses to boot there
 * too, so a misconfigured deployment fails loudly instead of rendering an
 * editor against production identity.
 */
export const environmentSchema = z.enum(["local", "dev", "staging"]);

export const authModeSchema = z.enum(["oidc", "dev-stub"]);

/**
 * Current /config.json contract version.
 *
 * A version is bumped whenever an existing valid file would stop validating.
 *
 * - v1 — initial shape; `auth.audience` optional, `auth.apiScope` absent.
 * - v2 (#25) — `auth.audience` removed and `auth.apiScope` required for
 *   `auth.mode: "oidc"`. Entra rejects the Auth0-style `audience` query
 *   parameter, so the delegated Editor Service API scope is requested as a
 *   scope instead. Migrating a v1 file: drop `auth.audience`, add
 *   `auth.apiScope: "api://<editor-api-app-id>/access_as_user"`, set
 *   `schemaVersion: 2`.
 * - v3 (#24) — the deploy-time `tenant` block removed. It was never sent to
 *   the Editor Service, which resolves the tenant per request from the token,
 *   so a pinned value could put one organisation's name on screen while every
 *   row came from another's. The tenant is the session's, or it is not shown.
 *   Migrating a v2 file: delete `tenant`, set `schemaVersion: 3`.
 */
export const RUNTIME_CONFIG_SCHEMA_VERSION = 3;

/**
 * Per-version migration text.
 *
 * An older file is refused by VERSION rather than by the individual fields the
 * bump changed — an operator sees "schemaVersion 2 is no longer supported",
 * not a confusing "unknown key: tenant".
 */
const SUPERSEDED_VERSION_MESSAGE: Record<number, string> = {
  1: `schemaVersion 1 is no longer supported: version ${RUNTIME_CONFIG_SCHEMA_VERSION} removes auth.audience and the tenant block, and requires auth.apiScope (#25, #24). Drop auth.audience and tenant, add auth.apiScope, and set schemaVersion to ${RUNTIME_CONFIG_SCHEMA_VERSION}.`,
  2: `schemaVersion 2 is no longer supported: version ${RUNTIME_CONFIG_SCHEMA_VERSION} removes the tenant block (#24) — the tenant is resolved per request by the Editor Service and read from the session, never pinned at deploy time. Drop tenant and set schemaVersion to ${RUNTIME_CONFIG_SCHEMA_VERSION}.`,
};

const runtimeConfigObject = z
  .object({
    schemaVersion: z.literal(RUNTIME_CONFIG_SCHEMA_VERSION, {
      error: (issue) =>
        (typeof issue.input === "number"
          ? SUPERSEDED_VERSION_MESSAGE[issue.input]
          : undefined) ??
        `schemaVersion must be ${RUNTIME_CONFIG_SCHEMA_VERSION}`,
    }),
    environment: environmentSchema,
    editorServiceBaseUrl: z.url(),
    auth: z
      .object({
        mode: authModeSchema,
        issuer: z.url().optional(),
        clientId: z.string().min(1).optional(),
        /**
         * The delegated Editor Service API scope (e.g.
         * api://<editor-api-app-id>/access_as_user). Supplied only as a public
         * value; issuer/audience validation configuration stays server-side.
         * The SPA requests it as an ordinary OIDC scope; it never relies on an
         * Auth0-style `audience` query parameter, which Entra rejects (#25).
         */
        apiScope: z
          .string()
          .trim()
          .min(1)
          .regex(/^api:\/\/[^/]+\/[^/]+$/, {
            message:
              "apiScope must be a single scope token in the form api://<app-id>/<role>",
          })
          .optional(),
        scopes: z
          .array(z.string().min(1))
          .default(["openid", "profile", "email"]),
        /**
         * Must be explicitly true for the development stub identity to be
         * usable, and even then only in the `local` environment. Defaults to
         * false so that omitting the flag can never weaken authentication.
         */
        allowDevStub: z.boolean().default(false),
      })
      .strict(),
    /**
     * #300 removed this block's second key. It served the Editor Service
     * contract from an in-browser mock so the shell could be run and
     * screenshotted with no backend, which made a second implementation of the
     * contract a selectable product lane. `.strict()` is what refuses it now:
     * a configuration still carrying the key fails closed on an unrecognised
     * key rather than silently ignoring it, so no deployment can quietly keep
     * shipping the flag after the code behind it is gone. The key itself is
     * named only where it is FORBIDDEN — `src/invariants/singleEditorService.test.ts`
     * sweeps the tree for it, and naming it here would be a hit.
     */
    features: z
      .object({
        preview: z.boolean().default(false),
        /**
         * The tenant tools: Talk now and the Environments tab on the Projects
         * page, the Lab's versions chip, and the automatic baseline sync of a
         * tenant-pack Project. Off unless a deployment turns them on.
         */
        tenantTools: z.boolean().default(false),
      })
      .strict()
      .default({ preview: false, tenantTools: false }),
    /**
     * The LOCAL DEVELOPER lane's controls (#567). Absent everywhere but the
     * default local Kubernetes overlay, and absence is the off switch: no
     * banner, no self-profile selector, no controller URL in the bundle's
     * reachable configuration.
     *
     * The cross-field rule below REFUSES this block outside the `local`
     * environment, so a cloud `/config.json` that somehow grows it fails the
     * pre-publication validator and, failing that, refuses to boot in the
     * browser — the same fail-closed posture as the dev-stub identity, for the
     * same reason: these are developer conveniences that must be structurally
     * unreachable from a deployed environment.
     */
    local: z
      .object({
        /**
         * Origin of the local access controller (#567's separately deployed
         * self-profile service). The client calls `<base>/local/v1/…`; in the
         * local overlay this is the same origin as the editor itself, routed
         * by path at the local Envoy edge.
         */
        accessControllerBaseUrl: z.url(),
        /**
         * Master switch for the role-simulation UI. Explicitly true rather
         * than inferred from the URL's presence, so a future local key cannot
         * accidentally arm the controls.
         */
        roleSimulation: z.boolean().default(false),
      })
      .strict()
      .optional(),
    /**
     * The deployment's CLAIM about which commit it rolled out (#490).
     *
     * `commit` used to hold `"dev"` on the Development overlay and `"staging"`
     * on Staging — moving labels that were never evidence of anything, and that
     * made a stale bundle undetectable from the browser. It is now written by
     * `scripts/pin-image-digests.py`, in the same edit that pins the image
     * digests, and the cross-field rule below requires an exact 40-hex commit
     * id in every environment but `local`.
     *
     * Still a CLAIM, though, and deliberately not the only source: this file is
     * a ConfigMap, so it can be restated after the fact while the pods keep
     * serving an older image. The bundle carries its own baked commit
     * (`src/config/buildInfo.ts`) and the two are compared — a stale rollout is
     * exactly the case where they disagree.
     *
     * Optional in the object and required by the refinement, rather than
     * required here: `local` legitimately has no commit to state, and a
     * required field would force the local lane to invent one.
     */
    release: z
      .object({
        appVersion: z.string().min(1),
        commit: z.string().min(1),
        serviceCommit: z
          .string()
          .regex(/^[0-9a-f]{40}$/)
          .optional(),
        deploymentCommit: z
          .string()
          .regex(/^[0-9a-f]{40}$/)
          .optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export const runtimeConfigSchema = runtimeConfigObject.superRefine(
  (config, ctx) => {
    // Issue #45. Every string the file carries, at any depth, is put through the
    // credential-shape detector before the cross-field rules run: `strict()` refuses
    // an unknown *key*, and this is what refuses a credential pasted into a key the
    // schema does define.
    rejectCredentialValues(config, [], ctx);

    if (config.auth.mode === "oidc") {
      for (const field of ["issuer", "clientId", "apiScope"] as const) {
        if (!config.auth[field]) {
          addSafeIssue(
            ctx,
            ["auth", field],
            `auth.${field} is required when auth.mode is "oidc"`,
          );
        }
      }
    }

    // #490. A deployed environment must state the exact commit it rolled out.
    //
    // Both halves matter and they fail differently: an ABSENT release block is a
    // deployment that never stated an intent, and a block carrying `dev`,
    // `staging` or `latest` is one that stated a moving label — which reads as an
    // answer and is not one. Neither is accepted outside `local`, where there is
    // no published image and therefore no commit to name.
    //
    // Lowercase exactly, not `GIT_SHA`'s case-insensitive form: every producer in
    // this estate writes lowercase (the OCI `org.opencontainers.image.revision`
    // label, `scripts/pin-image-digests.py`, the service's baked build-info), and
    // accepting mixed case here would let two spellings of one commit compare
    // unequal downstream while both passed validation.
    if (config.environment !== "local") {
      if (!config.release) {
        addSafeIssue(
          ctx,
          ["release"],
          `release.commit must name the deployed commit in the "${config.environment}" environment`,
        );
      } else if (!/^[0-9a-f]{40}$/.test(config.release.commit)) {
        // The rejected value is NOT quoted: it is the one field this file allows
        // to hold 40 hex characters, and a validator that echoed it would print
        // whatever a malformed config carries there into a deployment log.
        addSafeIssue(
          ctx,
          ["release", "commit"],
          "release.commit must be an exact 40-character lowercase commit id, not a moving label",
        );
      }
    }

    // #567. The local developer block is admitted in exactly one environment,
    // by the same reasoning as the dev-stub rule below: a controller URL or a
    // simulation switch reaching a deployed environment is a misdeployment, and
    // the browser refuses to boot on it rather than ignoring it.
    if (config.local !== undefined && config.environment !== "local") {
      addSafeIssue(
        ctx,
        ["local"],
        `the "local" developer block is only permitted in the local environment, not "${config.environment}"`,
      );
    }

    if (config.auth.mode === "dev-stub") {
      if (!config.auth.allowDevStub) {
        addSafeIssue(
          ctx,
          ["auth", "allowDevStub"],
          'auth.allowDevStub must be true to use the "dev-stub" identity',
        );
      }
      if (config.environment !== "local") {
        addSafeIssue(
          ctx,
          ["auth", "mode"],
          `the "dev-stub" identity is only permitted in the local environment, not "${config.environment}"`,
        );
      }
    }
  },
);

/**
 * Every property name the schema defines, at any depth.
 *
 * `scripts/validate-runtime-config.mjs` prints the *path* of each failing issue, and
 * a path segment is not automatically safe to print: under `strict()` an added key
 * becomes a path segment, and an added key can itself be credential material — a
 * webhook secret used as a JSON key is the case that motivated this. So the
 * validator prints a segment only if it appears here and redacts the rest.
 *
 * Derived by walking the schema rather than restated, so a field added above is
 * printable without anyone remembering to add it in two places.
 */
export const RUNTIME_CONFIG_FIELD_NAMES: ReadonlySet<string> = (() => {
  const names = new Set<string>();
  const visit = (schema: unknown): void => {
    // `unwrap()` peels `.optional()`, `.default()` and friends; `shape` exists only
    // on an object schema. Both are probed structurally because the wrapper classes
    // are internal to zod and not worth naming here.
    const candidate = schema as {
      shape?: Record<string, unknown>;
      unwrap?: () => unknown;
      def?: { innerType?: unknown; element?: unknown };
    };
    if (typeof candidate?.unwrap === "function") {
      visit(candidate.unwrap());
      return;
    }
    if (candidate?.def?.innerType !== undefined) {
      visit(candidate.def.innerType);
      return;
    }
    if (candidate?.def?.element !== undefined) {
      visit(candidate.def.element);
      return;
    }
    if (candidate?.shape === undefined) return;
    for (const [name, nested] of Object.entries(candidate.shape)) {
      names.add(name);
      visit(nested);
    }
  };
  visit(runtimeConfigObject);
  return names;
})();

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;
export type Environment = z.infer<typeof environmentSchema>;
export type AuthMode = z.infer<typeof authModeSchema>;
